// PR-DOCVAULT JUN03 — reusable per-entity document slot panel.
//
// Rendered inside the Vehicle Documents tab, the Driver Documents tab,
// and the Company Documents page. Each `slot` is one fixed kind for the
// entity (e.g. {slug:'istimara', label:'Istimara'}) and gets its own
// row: current file thumbnail/PDF tile, View, Replace, expiry date.
//
// Backend contract:
//   GET   /api/v1/<scope-prefix>/documents       → { items: [...] }
//   POST  /api/v1/<scope-prefix>/documents/:slug → multipart (file + optional expiryDate)
//   GET   /api/v1/<scope-prefix>/documents/:slug/file  → bytes (auth required)
//
// scope-prefix mapping:
//   vehicle  → /vehicles/:id
//   driver   → /drivers/:id
//   company  → /company
import { useEffect, useState, useCallback } from 'react';
import { Eye, FileText, RefreshCcw, Loader2, CalendarDays, CheckCircle2, AlertTriangle, AlertCircle, Save, Hash } from 'lucide-react';
import { PasteableUpload } from '@/components/common/PasteableUpload';
import { AuthedImage, viewFile } from '@/components/common/AuthedImage';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api';

export interface DocSlot {
  slug: string;
  label: string;
  required?: boolean;
  helperText?: string;
  /** Show a number box for this slot, labelled with this text. Omit for none. */
  numberLabel?: string;
  /** For slots that never expire, such as a profile picture. */
  noExpiry?: boolean;
}

export interface DocItem {
  id: string;
  kind: string;
  slug: string;
  mime: string | null;
  originalName: string | null;
  issueDate: string | null;
  expiryDate: string | null;
  documentNumber?: string | null;
  uploadedAt: string;
  url: string;
}

interface EntityDocumentsPanelProps {
  scope: 'vehicle' | 'driver' | 'company';
  /** Required for vehicle and driver scopes; ignored for company. */
  entityId?: string;
  slots: DocSlot[];
  /** Heading rendered above the slot list. */
  title?: string;
  /** Disable uploads (e.g. before a new entity is saved). */
  readOnly?: boolean;
}

export function EntityDocumentsPanel({
  scope, entityId, slots, title, readOnly,
}: EntityDocumentsPanelProps) {
  const [items, setItems] = useState<DocItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingExpiry, setEditingExpiry] = useState<Record<string, string>>({});
  const [editingNumber, setEditingNumber] = useState<Record<string, string>>({});

  const basePath = scope === 'company'
    ? '/api/v1/company/documents'
    : scope === 'vehicle'
      ? `/api/v1/vehicles/${entityId}/documents`
      : `/api/v1/drivers/${entityId}/documents`;

  const canUse = scope === 'company' || !!entityId;

  const load = useCallback(async () => {
    if (!canUse) return;
    setLoading(true);
    try {
      const { data } = await apiClient.get(basePath);
      setItems(data.items ?? []);
    } finally {
      setLoading(false);
    }
  }, [basePath, canUse]);

  useEffect(() => { load(); }, [load]);

  async function uploadFor(slug: string, file: File, expiryDate?: string, documentNumber?: string) {
    const fd = new FormData();
    fd.append('file', file);
    if (expiryDate) fd.append('expiryDate', expiryDate);
    if (documentNumber) fd.append('documentNumber', documentNumber);
    await apiClient.post(`${basePath}/${slug}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    await load();
  }

  // Update the expiry on an already-uploaded slot. Hits the dedicated
  // PATCH endpoint which also syncs the legacy column (vehicles.insurance_expiry,
  // drivers.iqama_expiry, …) so the dashboard + expiry engine see it.
  // documentNumber is only sent when the slot has a number box, so the vehicle
  // and company panels keep behaving exactly as before.
  async function patchDetails(slug: string, expiryDate: string | null, documentNumber?: string) {
    await apiClient.patch(`${basePath}/${slug}/expiry`, {
      expiryDate: expiryDate || null,
      ...(documentNumber === undefined ? {} : { documentNumber: documentNumber || null }),
    });
    await load();
  }


  if (!canUse) {
    return (
      <div className="text-sm text-muted-foreground italic px-1">
        Save first to upload documents.
      </div>
    );
  }

  const bySlug = new Map(items.map(i => [i.slug, i] as const));
  const filled = slots.filter(s => bySlug.has(s.slug)).length;
  const missingList = slots.filter(s => !bySlug.has(s.slug));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        {title
          ? <div className="text-xs uppercase tracking-widest text-muted-foreground">{title}</div>
          : <div />}
        <div className={
          'text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 ' +
          (filled === slots.length
            ? 'bg-emerald-500/10 text-emerald-700'
            : filled === 0
              ? 'bg-rose-500/10 text-rose-700'
              : 'bg-amber-500/10 text-amber-700')
        }>
          {filled === slots.length
            ? <CheckCircle2 className="h-3 w-3" />
            : <AlertCircle className="h-3 w-3" />}
          {filled} / {slots.length} done
        </div>
      </div>
      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      )}
      {!loading && missingList.length > 0 && (
        <div className="text-xs text-amber-700 bg-amber-500/5 border border-amber-500/20 rounded p-2">
          Still missing: <span className="font-medium">{missingList.map(s => s.label).join(', ')}</span>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {slots.map((slot) => {
          const current = bySlug.get(slot.slug);
          const expiryLocal = editingExpiry[slot.slug] ?? current?.expiryDate ?? '';
          const numberLocal = editingNumber[slot.slug] ?? current?.documentNumber ?? '';
          return (
            <DocSlotRow
              key={slot.slug}
              slot={slot}
              current={current}
              expiryValue={expiryLocal}
              numberValue={numberLocal}
              onExpiryChange={(v) => setEditingExpiry({ ...editingExpiry, [slot.slug]: v })}
              onNumberChange={(v) => setEditingNumber({ ...editingNumber, [slot.slug]: v })}
              onUpload={(f) => uploadFor(slot.slug, f, expiryLocal || undefined, numberLocal || undefined)}
              onSaveDetails={(exp, numb) => patchDetails(slot.slug, exp, numb)}
              readOnly={!!readOnly}
            />
          );
        })}
      </div>
    </div>
  );
}

function DocSlotRow({
  slot, current, expiryValue, numberValue, onExpiryChange, onNumberChange,
  onUpload, onSaveDetails, readOnly,
}: {
  slot: DocSlot;
  current: DocItem | undefined;
  expiryValue: string;
  numberValue: string;
  onExpiryChange: (v: string) => void;
  onNumberChange: (v: string) => void;
  onUpload: (f: File) => Promise<void>;
  onSaveDetails: (expiry: string | null, documentNumber?: string) => Promise<void>;
  readOnly: boolean;
}) {
  const [replacing, setReplacing] = useState(false);
  const [savingExpiry, setSavingExpiry] = useState(false);

  const isImage = current?.mime?.startsWith('image/');
  const isPdf   = current?.mime === 'application/pdf';

  const showNumber = !!slot.numberLabel;
  const showExpiry = !slot.noExpiry;

  const status = expiryStatus(current?.expiryDate ?? null);
  const expiryEdited = current && showExpiry && (expiryValue || '') !== (current.expiryDate ?? '');
  const numberEdited = current && showNumber && (numberValue || '') !== (current.documentNumber ?? '');
  const edited = expiryEdited || numberEdited;

  async function commitDetails() {
    if (!current) return;
    setSavingExpiry(true);
    try {
      await onSaveDetails(expiryValue || null, showNumber ? numberValue : undefined);
    } finally { setSavingExpiry(false); }
  }

  return (
    <div className={
      'rounded-lg border p-3 bg-card space-y-3 ' +
      (current ? (status === 'expired' ? 'border-rose-500/40'
                : status === 'soon'    ? 'border-amber-500/40'
                : 'border-emerald-500/30')
              : 'border-border')
    }>
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium text-sm flex items-center gap-2">
          {current
            ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            : <AlertCircle  className="h-4 w-4 text-rose-500" />}
          {slot.label}
          {slot.required && !current && <span className="text-[10px] text-amber-600">required</span>}
          {status === 'expired' && <span className="text-[10px] text-rose-600 font-semibold uppercase">expired</span>}
          {status === 'soon'    && <span className="text-[10px] text-amber-600 font-semibold uppercase">expiring</span>}
        </div>
        {current && !replacing && !readOnly && (
          <Button size="sm" variant="ghost" onClick={() => setReplacing(true)} className="gap-1.5">
            <RefreshCcw className="h-3.5 w-3.5" /> Replace
          </Button>
        )}
      </div>

      {current && !replacing && (
        <div className="space-y-2">
          {isImage ? (
            <AuthedImage
              src={current.url}
              alt={current.originalName ?? slot.label}
              className="rounded border border-border max-h-44 object-contain bg-muted/30 w-full"
            />
          ) : isPdf ? (
            <div className="flex items-center gap-2 p-3 rounded border border-border bg-muted/30 text-sm">
              <FileText className="h-5 w-5" />
              <div className="flex-1 truncate">{current.originalName ?? 'document.pdf'}</div>
            </div>
          ) : (
            <div className="text-xs text-muted-foreground italic">{current.originalName}</div>
          )}
          {/* Number — typed by hand off the document itself. */}
          {showNumber && (
            <div className="flex items-center gap-2">
              <Hash className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground shrink-0">{slot.numberLabel}</span>
              <input
                type="text"
                value={numberValue}
                maxLength={120}
                onChange={(e) => onNumberChange(e.target.value)}
                className="text-xs px-2 py-1 rounded border border-border bg-background flex-1 min-w-0 font-mono"
                aria-label={slot.numberLabel}
                disabled={readOnly}
                dir="ltr"
              />
            </div>
          )}
          {/* Expiry — editable in place, propagates to the legacy column. */}
          {showExpiry && (
            <div className="flex items-center gap-2">
              <CalendarDays className={
                'h-3.5 w-3.5 ' +
                (status === 'expired' ? 'text-rose-600' : status === 'soon' ? 'text-amber-600' : 'text-muted-foreground')
              } />
              <span className="text-xs text-muted-foreground">Expires</span>
              <input
                type="date"
                value={expiryValue}
                onChange={(e) => onExpiryChange(e.target.value)}
                className="text-xs px-2 py-1 rounded border border-border bg-background flex-1 min-w-0"
                aria-label={slot.label + ' expiry date'}
                disabled={readOnly}
              />
            </div>
          )}
          {/* One Save for both boxes, so a number and a date can be corrected
              together rather than in two round trips. */}
          {edited && !readOnly && (
            <Button size="sm" variant="outline" onClick={commitDetails} disabled={savingExpiry} className="gap-1 px-2 w-full">
              {savingExpiry ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Save changes
            </Button>
          )}
          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>uploaded {current.uploadedAt.slice(0,10)}</span>
            <Button size="sm" variant="outline" onClick={() => viewFile(current.url)} className="gap-1.5">
              <Eye className="h-3.5 w-3.5" /> View
            </Button>
          </div>
        </div>
      )}

      {(!current || replacing) && !readOnly && (
        <>
          {/* Both are filled in before the file is chosen, then sent with it. */}
          {showNumber && (
            <div className="flex items-center gap-2">
              <Hash className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="text"
                value={numberValue}
                maxLength={120}
                onChange={(e) => onNumberChange(e.target.value)}
                className="text-xs px-2 py-1 rounded border border-border bg-background flex-1 min-w-0 font-mono"
                placeholder={slot.numberLabel}
                aria-label={slot.numberLabel}
                dir="ltr"
              />
            </div>
          )}
          {showExpiry && (
            <div className="flex items-center gap-2">
              <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                type="date"
                value={expiryValue}
                onChange={(e) => onExpiryChange(e.target.value)}
                className="text-xs px-2 py-1 rounded border border-border bg-background w-40"
                aria-label={slot.label + ' expiry date'}
              />
            </div>
          )}
          <PasteableUpload
            onUpload={async (f) => {
              await onUpload(f);
              setReplacing(false);
            }}
            accept="image/*,application/pdf"
            maxSizeMB={10}
            helperText={slot.helperText ?? 'JPG, PNG or PDF, up to 10 MB'}
          />
          {replacing && (
            <Button size="sm" variant="ghost" onClick={() => setReplacing(false)}>
              Cancel replace
            </Button>
          )}
        </>
      )}
    </div>
  );
}

// Returns the expiry "bucket" for an ISO date string (YYYY-MM-DD).
// expired: already past; soon: within 30 days; otherwise ok/none.
function expiryStatus(iso: string | null): 'expired' | 'soon' | 'ok' | 'none' {
  if (!iso) return 'none';
  const d = new Date(iso + 'T00:00:00');
  const now = new Date();
  const ms = d.getTime() - now.getTime();
  if (ms < 0) return 'expired';
  if (ms < 30 * 24 * 60 * 60 * 1000) return 'soon';
  return 'ok';
}

// Slot catalogues — exported so VehicleForm/DriverForm/etc. import a
// single source of truth.
export const VEHICLE_SLOTS: DocSlot[] = [
  { slug: 'istimara',            label: 'Istimara (registration)',  required: true  },
  { slug: 'operation_card',      label: 'Operation card (Naqliat)' },
  { slug: 'insurance',           label: 'Insurance certificate' },
  { slug: 'periodic_inspection', label: 'Periodic inspection (Fahas)' },
];

export const DRIVER_SLOTS: DocSlot[] = [
  { slug: 'driver_card',     label: 'Driver card',              numberLabel: 'Card number' },
  // "Muqeem ID" is what the Saudi residency portal calls the Iqama number, so
  // the label says both. The slug and the stored kind stay 'iqama'.
  { slug: 'iqama',           label: 'Muqeem ID (Iqama)',        numberLabel: 'Iqama number', required: true },
  { slug: 'driving_license', label: 'Driving licence (Ruksa)',  numberLabel: 'Licence number' },
  { slug: 'insurance',       label: 'Insurance',                numberLabel: 'Policy number' },
];

/** The profile picture. Its own slot because it has neither number nor expiry. */
export const DRIVER_PHOTO_SLOT: DocSlot[] = [
  { slug: 'photo', label: 'Profile photo', noExpiry: true, helperText: 'JPG or PNG, up to 10 MB' },
];

export const COMPANY_SLOTS: DocSlot[] = [
  { slug: 'cr', label: 'Commercial Registration (CR)', required: true },
];
